/**
 * Pure, DOM-free helpers for the Creative Performance grid: its infinite-scroll page size, the sortable-column
 * menu, the default sort direction per column, and page flattening. Kept out of the view component so
 * they can be unit-tested without a query client or a DOM.
 */
import type {
    ICreativeCustomWindow,
    ICreativeGridRange,
    ICreativeGridSortBy,
    ICreativeGridSortDir,
    ICreativePerformanceResponse,
    ICreativePerformanceRow,
} from '@/api/creative-intelligence/get-creative-performance-query'
import type { IGroupedCreativePerformanceSegment } from '@/api/creative-intelligence/get-grouped-creative-performance-query'
import type {
    ICreativePerformanceFilterState,
    ICreativePerformanceSortRule,
} from '@/features/creative-performance/creative-performance-analysis-state'
import { EMPTY_CREATIVE_FILTERS } from '@/features/creative-performance/creative-performance-filter-helpers'

/** Page size for the grid's infinite scroll — divisible by the 2/3/4-column breakpoints for clean rows. */
export const CREATIVE_GRID_PAGE_SIZE = 48

/** The analysis-window options for the date-range control, in menu order. */
export const CREATIVE_RANGES: { value: ICreativeGridRange; label: string }[] = [
    { value: '7d', label: 'Last 7 days' },
    { value: '14d', label: 'Last 14 days' },
    { value: '30d', label: 'Last 30 days' },
    { value: '60d', label: 'Last 60 days' },
    { value: '90d', label: 'Last 90 days' },
]

/**
 * The detail panel's own window options: the grid's four, plus one the grid cannot offer.
 *
 * `lastActive` is resolved SERVER-side to the block around the creative's most recent delivering day.
 * It exists because the creative a buyer most wants to review is usually one that stopped — and over
 * "Last 7 days" a paused creative reads as a column of zeros, which says nothing about the creative.
 */
export const CREATIVE_DETAIL_RANGES: { value: ICreativeDetailRange; label: string }[] = [
    ...CREATIVE_RANGES,
    { value: 'lastActive', label: 'Last active period' },
]

/** The panel's window, which may be one of the grid's four or the data-resolved `lastActive`. */
export type ICreativeDetailRange = ICreativeGridRange | 'lastActive'

/** The widest window on offer — past it, "try a longer window" is not advice anyone can take. */
export const WIDEST_CREATIVE_RANGE: ICreativeGridRange = '90d'

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/**
 * A custom window's label — the dates themselves, because that is the only thing that names it.
 *
 * Both endpoints carry their year when they differ, so a window straddling a year boundary cannot
 * read as a fourteen-month one. Formatted from the date parts rather than through a `Date`, which
 * would shift a `YYYY-MM-DD` into the viewer's timezone and print the wrong day west of UTC.
 */
export function creativeWindowLabel(window: ICreativeCustomWindow): string {
    const format = (date: string, withYear: boolean) => {
        const [year, month, day] = date.split('-')
        const monthName = MONTH_LABELS[Number(month) - 1] ?? ''
        return withYear ? `${monthName} ${Number(day)}, ${year}` : `${monthName} ${Number(day)}`
    }
    const sameYear = window.since.slice(0, 4) === window.until.slice(0, 4)
    if (window.since === window.until) return format(window.until, true)
    return `${format(window.since, !sameYear)} – ${format(window.until, true)}`
}

/** The window's human label (for the detail modal's header and the range control's trigger). */
export function creativeRangeLabel(range: ICreativeDetailRange, customWindow?: ICreativeCustomWindow | null): string {
    if (range === 'custom') return customWindow ? creativeWindowLabel(customWindow) : 'Custom'
    return CREATIVE_DETAIL_RANGES.find((r) => r.value === range)?.label ?? ''
}

/**
 * The ad count on a creative card, and the sentence that stops it being misread.
 *
 * `adCount` counts AD ENTITIES sharing one `ProviderCreative` — one asset that a buyer duplicated
 * across ad sets or campaigns — and it is the single most misread figure on the surface: read as a
 * count of creatives, `4 ads` beside ONE preview looks like three missing thumbnails rather than one
 * asset running four times. The label therefore names whose count it is, and the explainer says the
 * two things a reader cannot see: the panel's figures are the TOTAL across those ads, and only ads
 * that delivered in the window are members (the grouping's `deliveredInWindow` rule), so an ad that
 * sat paused all window is legitimately absent from the count.
 *
 * Copy lives here, not in the four components that show it, so the modal header, the ads table, the
 * grid card and the table row cannot drift into four different explanations of one number.
 */
export function adCountLabel(adCount: number): string {
    return `${adCount} ${adCount === 1 ? 'ad' : 'ads'}`
}

/** `Used by 4 ads` — the count with its subject attached, for surfaces with room for the words. */
export function adUsageLabel(adCount: number): string {
    return `Used by ${adCountLabel(adCount)}`
}

/** The hover explanation behind the count. Plain text: it is also used as a native `title`. */
export function adCountExplainer(adCount: number): string {
    if (adCount <= 1) {
        return 'One ad uses this creative in this window, so every figure here is that single ad\u2019s own.'
    }
    return (
        `The same creative runs as ${adCount} separate ads, usually across different ad sets \u2014 so there is ` +
        `one preview, not ${adCount}. Every figure here is the total across all ${adCount}, with ROAS and costs ` +
        'recomputed from those totals. Only ads that delivered in this window are counted.'
    )
}

/**
 * `ProviderCreativeType` → the ONE word the product uses for it, shared by every surface that names
 * a creative's type: the grid badge, the table's Creative type column, the thumbnail tile, the
 * detail modal and the detail media header.
 *
 * One map, not five. `RESPONSIVE`/`FLEXIBLE` both read as "Dynamic" — the provider's two words for
 * an asset-feed ad — and the Creative type FILTER publishes the same labels from the backend field
 * registry, because a value the grid calls "Dynamic" and the filter calls "Responsive" is a value
 * the user cannot find.
 */
export const CREATIVE_TYPE_LABEL: Record<string, string> = {
    VIDEO: 'Video',
    IMAGE: 'Image',
    CATALOG: 'Catalog',
    RESPONSIVE: 'Dynamic',
    FLEXIBLE: 'Dynamic',
    CAROUSEL: 'Carousel',
    COLLECTION: 'Collection',
    OTHER: 'Other',
}

/**
 * The BADGE label for a creative type — what an overlay on the artwork is allowed to say.
 *
 * `OTHER` deliberately yields nothing: it is the provider declining to name the format, so a badge
 * reading "Other" costs a corner of the creative to say less than the media already shows. Every
 * badge surface then falls back to what it actually resolved ("Video", "No art", …). A column
 * REPORTING the field wants the honest value instead and reads `CREATIVE_TYPE_LABEL` directly.
 */
export function creativeTypeLabel(type: string | null | undefined) {
    if (!type || type === 'OTHER') return null
    return CREATIVE_TYPE_LABEL[type] ?? null
}

/** The two ways the grid can present the same paginated set. Reflected in the page's `view` param. */
export type ICreativeGridView = 'grid' | 'table'

/** The columns the grid can sort by, in menu order. Mirrors the backend `sortBy` enum. */
export const CREATIVE_SORT_FIELDS: { value: ICreativeGridSortBy; label: string }[] = [
    { value: 'spend', label: 'Spend' },
    { value: 'roas', label: 'ROAS' },
    { value: 'revenue', label: 'Purchase value' },
    { value: 'cpa', label: 'Cost / purchase' },
    { value: 'impressions', label: 'Impressions' },
    { value: 'name', label: 'Name' },
]

/** The natural first direction when switching sort column: names read A→Z, metrics best-first (desc). */
export function defaultSortDir(sortBy: ICreativeGridSortBy): ICreativeGridSortDir {
    return sortBy === 'name' ? 'asc' : 'desc'
}

/** Stable per-row identity (creative id, or the first collapsed ad id for null-creative singletons). */
export function creativeRowKey(row: ICreativePerformanceRow): string {
    return row.creativeId ?? row.adEntityIds[0] ?? row.name
}

/**
 * Split a selection into the rows a bulk annotation can actually write to. Selection keys on
 * `creativeRowKey` so every visible row is selectable, but only rows with a synced
 * `ProviderCreative` (a `creativeUuid`) have anywhere to persist a status — the rest are counted
 * and DISCLOSED, never silently dropped.
 */
export function partitionAnnotatableRows(rows: ICreativePerformanceRow[], selectedKeys: ReadonlySet<string>) {
    const selected = rows.filter((row) => selectedKeys.has(creativeRowKey(row)))
    const eligible = selected.filter((row) => Boolean(row.creativeUuid))
    return { eligible, skippedCount: selected.length - eligible.length }
}

/**
 * The bulk write loop: sequential (each write patches the caches in place; parallel fire would gain
 * nothing user-visible and hammer the API), failures counted per row rather than aborting the run —
 * a creative that failed mid-batch must not strand the rest unwritten with no accounting.
 */
export async function runBulkStatusAnnotation({
    rows,
    selectedKeys,
    patch,
}: {
    rows: ICreativePerformanceRow[]
    selectedKeys: ReadonlySet<string>
    /** One creative's write — the caller binds the mutation and the status. */
    patch: (creativeUuid: string) => Promise<unknown>
}) {
    const { eligible, skippedCount } = partitionAnnotatableRows(rows, selectedKeys)
    let failedCount = 0
    for (const row of eligible) {
        try {
            await patch(row.creativeUuid!)
        } catch {
            failedCount += 1
        }
    }
    return {
        eligibleCount: eligible.length,
        appliedCount: eligible.length - failedCount,
        failedCount,
        skippedCount,
    }
}

/**
 * The user-facing verdict of a bulk run. Every branch discloses the skipped remainder — rows that
 * were selected but have no synced record are counted, never silently dropped.
 */
export function bulkStatusAnnotationToast(
    result: Awaited<ReturnType<typeof runBulkStatusAnnotation>>,
    statusLabel: string | null
): { kind: 'success' | 'error'; message: string } {
    const { eligibleCount, appliedCount, failedCount, skippedCount } = result
    const skippedNote = skippedCount > 0 ? ` · ${skippedCount} skipped (no synced record)` : ''
    if (eligibleCount === 0) {
        return {
            kind: 'error',
            message: 'None of the selected creatives have a synced record yet, so they can’t be annotated.',
        }
    }
    if (failedCount > 0) {
        return { kind: 'error', message: `Couldn’t update ${failedCount} of ${eligibleCount} creatives${skippedNote}` }
    }
    const noun = appliedCount === 1 ? 'creative' : 'creatives'
    return {
        kind: 'success',
        message: statusLabel
            ? `Marked ${appliedCount} ${noun} as ${statusLabel}${skippedNote}`
            : `Cleared status on ${appliedCount} ${noun}${skippedNote}`,
    }
}

/**
 * The identity of the current result set, for selection lifetime: a row key (`creativeRowKey`)
 * only means something within one filtered/sorted/windowed set, so the selection clears when this
 * key changes. Group EXPANSION is deliberately absent — collapsing hides rows without changing the
 * set's identity, and the view intersects the selection with the visible rows instead.
 */
export function creativeSelectionResetKey(parts: {
    range: string
    customWindow: unknown
    filters: unknown
    filterState: unknown
    sorts: unknown
    groupBy: readonly string[]
    view: string
}) {
    return [
        parts.range,
        JSON.stringify(parts.customWindow),
        JSON.stringify(parts.filters),
        JSON.stringify(parts.filterState),
        JSON.stringify(parts.sorts),
        parts.groupBy.join(','),
        parts.view,
    ].join('|')
}

/**
 * Flatten react-query infinite pages into one row list, deduped by `creativeRowKey`. Offset paging
 * over a set the server recomputes per request can return a boundary creative in two adjacent windows
 * if the underlying metrics shift mid-scroll; without a dedupe pass that surfaces as duplicate React
 * keys. First occurrence wins so the server's sort order is preserved. Tolerates an undefined page set.
 */
export function flattenCreativeRows(
    pages: ICreativePerformanceResponse[] | undefined,
): ICreativePerformanceRow[] {
    const seen = new Set<string>()
    const rows: ICreativePerformanceRow[] = []
    for (const page of pages ?? []) {
        for (const row of page.data) {
            const key = creativeRowKey(row)
            if (seen.has(key)) continue
            seen.add(key)
            rows.push(row)
        }
    }
    return rows
}

/**
 * The expanded nodes that still owe CREATIVE rows. A node is pending when it is expanded (its path
 * key is in `expandedGroups`, which feeds the backend's `includeRows`) but the merged segments carry
 * no rows for it — exactly "the group the user just expanded, still resolving".
 *
 * Only a DEEPEST-level node can be pending. An expanded node with sub-groups owes those sub-groups,
 * which arrive as their own segments; flagging it would park a card skeleton under a header whose
 * children are headers. Collapsed nodes render no row area, and a node with loaded rows renders
 * them, so the empty-row test stays unambiguous.
 */
export function pendingGroupKeys(
    segments: IGroupedCreativePerformanceSegment[],
    expandedGroups: string[],
): string[] {
    if (expandedGroups.length === 0) return []
    const expanded = new Set(expandedGroups)
    return segments
        .filter(
            (segment) =>
                !segment.group.hasChildGroups && expanded.has(segment.group.key) && segment.rows.length === 0,
        )
        .map((segment) => segment.group.key)
}

/** The row-shaped projection of a group's aggregate metrics — what the shared card metrics read. */
export type GroupAggregateRow = Pick<
    ICreativePerformanceRow,
    | 'spendMicros'
    | 'revenueMicros'
    | 'impressions'
    | 'roas'
    | 'costPerPurchaseMicros'
    | 'purchaseConversions'
    | 'results'
    | 'costPerResultMicros'
    | 'mixedObjectives'
    | 'resultBreakdown'
    | 'terminalConversions'
    // Every remaining metric the catalog can put on a card. A group header renders through the SAME
    // formatters a creative row does, so a field absent here is a metric that silently dashes on
    // every header while reading correctly one row below — which looks like a data gap rather than a
    // projection gap.
    | 'aovMicros'
    | 'conversionRate'
    | 'revenuePerVisitorMicros'
    | 'cpaVsTarget'
    | 'spendShareOfAdSet'
    | 'spendShareOfCampaign'
    | 'spendShareOfAccount'
    | 'cpmMicros'
    | 'frequency'
    | 'effectiveStatus'
    | 'outboundClicks'
    | 'outboundCtr'
    | 'landingPageViews'
    | 'landingPageViewRate'
    | 'addsToCart'
    | 'addToCartRate'
    | 'costPerAddToCartMicros'
    | 'hookRate'
    | 'threeSecondPlays'
    | 'daysActive'
    | 'adCount'
    | 'outboundCtrChange'
    | 'cpmChange'
    | 'cpaChange'
    | 'cpaVsOwnPast'
    // The metrics promoted from the detail panel. A group header renders through the same formatters
    // a creative row does, so a field absent here dashes on every header while reading correctly one
    // row below — which looks like a data gap rather than a projection gap.
    | 'videoPlays'
    | 'thruPlays'
    | 'costPerThruPlayMicros'
    | 'holdRate'
    | 'firstFrameRetention'
    | 'retentionP25'
    | 'retentionP50'
    | 'retentionP75'
    | 'retentionP100'
    | 'twoSecondPlays'
    | 'clicks'
    | 'ctr'
    | 'cpcMicros'
    | 'linkClicks'
    | 'linkCtr'
    | 'costPerLinkClickMicros'
    | 'uniqueClicks'
    | 'postEngagement'
    | 'engagementRate'
    | 'costPerEngagementMicros'
    | 'initiatedCheckouts'
    | 'checkoutCompletionRate'
> & {
    /**
     * Whether this row's return came from a PURCHASE — a property of the ACCOUNT, not of the row, so
     * the caller stamps it from the grid's outcome model. `undefined` reads as do-not-withhold, which
     * is today's behaviour and what a stale cached response renders.
     */
    isPurchaseOutcome?: boolean
    /**
     * True when the view thinned on an ADAPTIVE floor that fell below the house confidence floor.
     *
     * The rows are shown — an empty view is worse than a thin one — but the ratios behind them rest
     * on fewer conversions than the house says a verdict needs, so the colour is withheld. Showing
     * rows and withholding judgement is the honest combination; showing both would be worse than
     * showing neither.
     */
    verdictFloorLowered?: boolean
}

/**
 * Metric cards run on row-shaped data, so a group header passes a fabricated row (its aggregate
 * metrics) through the same formatter the flat grid uses. The table's `GroupHeaderRow` does the
 * same fabrication inline; this builder keeps one definition of "a group as a metric row".
 */
export function groupAggregateRow(segment: IGroupedCreativePerformanceSegment): GroupAggregateRow {
    const metrics = segment.group.metrics
    return {
        ...metrics,
        // The header stands for many creatives, so anything that is a property of ONE ad is absent
        // rather than aggregated. The server already withholds the changes, frequency and days
        // active for the same reason; delivery status is withheld here because a group routinely
        // spans an active creative and a paused one, and naming either would assert a state the
        // group does not have.
        effectiveStatus: '',
        adCount: 0,
    }
}

/**
 * The single URL-state write a translated AI filter produces.
 *
 * Two decisions live here rather than in the component, because both are behavioural rather than
 * visual:
 *
 *  - The expression REPLACES what was applied. A query describes a whole set, so merging it with
 *    conditions the user did not restate would produce a set neither the user nor the query asked
 *    for. The legacy readable bounds are cleared for the same reason the manual path clears them —
 *    they have already been folded into the expression.
 *  - The window is written ONLY when the query named a period. A query with no period is not a
 *    request to reset the window to a default; leaving it out is what keeps the user's own window.
 *
 * One object, so the expression and the window land in ONE history entry and the grid refetches once.
 */
export function creativeAiFilterStateUpdate(translation: {
    filterState: ICreativePerformanceFilterState
    sort?: { field: string; direction: string } | null
    window?: { range: string; since?: string; until?: string } | null
}) {
    const window = translation.window
    const windowUpdate = !window
        ? {}
        : window.range === 'custom' && window.since && window.until
          ? { range: 'custom' as const, customWindow: { since: window.since, until: window.until } }
          : { range: window.range as ICreativeGridRange, customWindow: null }

    // A ranking is how a qualitative question ("best performing ads") gets answered: it reorders the
    // whole set instead of guessing a threshold that might match none of it. Written only when the
    // query implied one, so a purely conditional query leaves the user's own sort alone.
    const sortUpdate = translation.sort ? { sorts: [translation.sort as ICreativePerformanceSortRule] } : {}

    // An EMPTY expression is never written. A query naming only a period ("last 30 days") or only a
    // ranking ("best performing ads") resolves to no conditions, and writing that would clear every
    // condition the user already had while they were only nudging the window or the order — the same
    // silent loss the failed-translation guard exists to prevent, reached through another branch.
    // Omitting both keys leaves the expression AND the legacy readable bounds as they were, since
    // `setState` merges.
    //
    // "Show me last 30 days with no filters" is indistinguishable here from "last 30 days", so the
    // non-destructive reading wins; clearing stays the Reset control's job, one click away.
    if (translation.filterState.groups.length === 0) return { ...sortUpdate, ...windowUpdate }

    return {
        filters: EMPTY_CREATIVE_FILTERS,
        filterState: translation.filterState,
        ...sortUpdate,
        ...windowUpdate,
    }
}

/**
 * Whether the surface owes the reader a skeleton rather than a result.
 *
 * True while a read has produced nothing yet — which now includes a date change, since the previous
 * window's placeholder is deliberately dropped rather than shown under the new window's label. Also
 * true while a RETAINED result is empty: captioning the previous key's empty set for the new state
 * showed "No creatives in this window" over a set that was about to come back full. An empty
 * placeholder is a loading state, not an answer.
 *
 * `shown` is what is currently on screen — creative rows plus grouped segments.
 */
export function creativePerformanceIsLoading(query: { isLoading: boolean; isPlaceholderData: boolean }, shown: number) {
    return query.isLoading || (query.isPlaceholderData && shown === 0)
}
